import React, { useEffect, useMemo, useState } from "react";
import { createInstance } from "i18next";
import { I18nextProvider } from "react-i18next";
import { Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { UserProvider } from "../contexts/UserContext";
import theme from "../theme";
import { isSupportedLanguage, normalizeLanguageCode } from "../supportedLanguages";
import { getLanguagePreference } from "../utils/userPreferences";
import { getMicrosoftStoreUrl, StorePlacement } from "../utils/microsoftStore";
import HomePage from "./HomePage";
import JoinIn from "./JoinIn";
import FeaturesPage from "./FeaturesPage";
import SampleVideosPage from "./SampleVideosPage";
import YouTubeRankingsPage from "./YouTubeRankingsPage";
import Pricing from "./Pricing";
import HowToUse from "./HowToUse";
import Download from "./Download";
import FAQPage from "./FAQPage";
import SupportPage from "./SupportPage";
import SupportChatbot from "./SupportChatbot";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";
import RefundPolicy from "./RefundPolicy";
import AIContentPolicy from "./AIContentPolicy";
import BlogIndexPage from "./BlogIndexPage";
import BlogPostPage from "./BlogPostPage";
import UpdateNotesPage from "./UpdateNotesPage";
import CookieConsentBanner from "./CookieConsentBanner";
import FloatingSupportChat from "./FloatingSupportChat";
import Footer from "./Footer";
import UserStatus from "./UserStatus";
import UseCasePage from "./UseCasePage";
import styles from "./StaticPublicPage.module.css";

export type StaticPageBootstrap = {
  language: string;
  routePath: string;
  storeUrl?: string;
  translations: Record<string, unknown>;
};

const publicPages: Record<string, React.ComponentType> = {
  "/": HomePage,
  "/joinIn": JoinIn,
  "/features": FeaturesPage,
  "/sample-videos": SampleVideosPage,
  "/youtube-rankings": YouTubeRankingsPage,
  "/pricing": Pricing,
  "/how-to-use": HowToUse,
  "/download": Download,
  "/faq": FAQPage,
  "/support": SupportPage,
  "/support/chatbot": SupportChatbot,
  "/privacy-policy": PrivacyPolicy,
  "/terms-of-service": TermsOfService,
  "/refund-policy": RefundPolicy,
  "/ai-content-policy": AIContentPolicy,
  "/blog": BlogIndexPage,
  "/updates": UpdateNotesPage,
};

const storePlacements = new Set<StorePlacement>([
  "home_hero", "home_workflow", "home_footer", "download_page",
  "features_page", "youtube_rankings", "ai_shorts_page",
  "marketing_video_page", "updates_page",
]);

function ClientExtras() {
  const [ready, setReady] = useState(false);

  useEffect(() => setReady(true), []);

  if (!ready) {
    return null;
  }

  return (
    <>
      <FloatingSupportChat />
      <CookieConsentBanner />
    </>
  );
}

function RedirectChangedRoute({ language, routePath }: { language: string; routePath: string }) {
  const location = useLocation();

  useEffect(() => {
    const expected = `/${language}${routePath === "/" ? "" : routePath}`;
    const current = location.pathname.replace(/\/$/, "") || "/";
    if (current === expected || (routePath === "/" && current === "/")) {
      return;
    }
    window.location.assign(location.pathname + location.search + location.hash);
  }, [language, routePath, location]);

  return null;
}

function getPage(routePath: string, language: string, storeUrl?: string) {
  if (routePath === "/ai-shorts-maker" || routePath === "/marketing-video-maker") {
    const slug = routePath.slice(1) as "ai-shorts-maker" | "marketing-video-maker";
    return <UseCasePage slug={slug} language={language as "en" | "ko" | "ja"} initialStoreUrl={storeUrl} />;
  }
  if (routePath.startsWith("/blog/")) {
    return <BlogPostPage />;
  }
  if (routePath.startsWith("/updates/")) {
    return <UpdateNotesPage />;
  }
  const Page = publicPages[routePath];
  if (!Page) {
    throw new Error(`Unsupported static page: ${routePath}`);
  }
  return <Page />;
}

function getRoutePattern(routePath: string) {
  if (routePath.startsWith("/blog/")) {
    return "/:lang/blog/:slug";
  }
  if (routePath.startsWith("/updates/")) {
    return "/:lang/updates/:slug";
  }
  return routePath === "/" ? "/:lang" : `/:lang${routePath}`;
}

export default function StaticPublicPage(props: StaticPageBootstrap) {
  const { language, routePath, storeUrl, translations } = props;
  const translationInstance = useMemo(() => {
    const instance = createInstance();
    instance.init({
      lng: language,
      fallbackLng: false,
      resources: { [language]: { translation: translations } },
      initImmediate: false,
      interpolation: { escapeValue: false },
    });
    return instance;
  }, [language, translations]);

  useEffect(() => {
    window.autovidStaticHydrating = false;
  }, []);

  useEffect(() => {
    if (routePath !== "/" || window.location.pathname !== "/") {
      return;
    }
    const savedLanguage = normalizeLanguageCode(getLanguagePreference());
    if (isSupportedLanguage(savedLanguage) && savedLanguage !== "en") {
      window.location.replace(`/${savedLanguage}`);
    }
  }, [routePath]);

  function openLocalLink(event: React.MouseEvent<HTMLDivElement>) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const target = event.target as HTMLElement;
    const link = target.closest("a");
    if (!link || link.target || link.hasAttribute("download")) {
      return;
    }
    const destination = new URL(link.href);
    if (destination.hostname === "apps.microsoft.com") {
      const campaignId = destination.searchParams.get("cid") ?? "";
      const placement = campaignId.replace(/^autovid_web_/, "") as StorePlacement;
      if (storePlacements.has(placement)) {
        link.href = getMicrosoftStoreUrl(placement);
      }
      return;
    }
    if (destination.origin !== window.location.origin) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    window.location.assign(destination.href);
  }

  return (
    <I18nextProvider i18n={translationInstance}>
      <ThemeProvider theme={theme}>
        <UserProvider>
          <div className={styles.layout} onClickCapture={openLocalLink}>
            <UserStatus />
            <RedirectChangedRoute language={language} routePath={routePath} />
            <main className={styles.main}>
              <Routes>
                <Route
                  path={getRoutePattern(routePath)}
                  element={getPage(routePath, language, storeUrl)}
                />
                {routePath === "/" && (
                  <Route path="/" element={getPage(routePath, language, storeUrl)} />
                )}
              </Routes>
            </main>
            <Footer />
            <ClientExtras />
          </div>
        </UserProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
}
